export interface HlsManifestInfo {
    codecs: string[];
    formats: string[];
    hasAtmos: boolean;
    hasHiResLossless: boolean;
    hasLossless: boolean;
}

type RewriteUrl = (url: string) => string | Promise<string>;

function collectCodecs(manifestText: string): string[] {
    const codecs = new Set<string>();
    const codecsRegex = /CODECS=(?:"([^"]+)"|([^,\s]+))/gi;
    let match: RegExpExecArray | null;

    while ((match = codecsRegex.exec(manifestText))) {
        const value = match[1] || match[2] || '';
        value.split(',').forEach((codec) => {
            const normalized = codec.trim().toLowerCase();
            if (normalized) codecs.add(normalized);
        });
    }

    return [...codecs];
}

function hasHiResSampleRate(manifestText: string): boolean {
    const sampleRateRegex = /(?:AUDIO-)?SAMPLE-RATE=(?:"?)(\d+)/gi;
    let match: RegExpExecArray | null;

    while ((match = sampleRateRegex.exec(manifestText))) {
        if (Number(match[1]) > 48000) return true;
    }

    return false;
}

export function analyzeHlsManifest(manifestText: string, formats: string[] = []): HlsManifestInfo {
    const normalizedText = manifestText.toLowerCase();
    const normalizedFormats = formats.map((format) => format.toUpperCase());
    const codecs = collectCodecs(manifestText);
    const hasFlacCodec = codecs.some((codec) => codec.includes('flac')) || normalizedText.includes('flac');
    const hasAlacCodec = codecs.some((codec) => codec.includes('alac')) || normalizedText.includes('alac');
    const hasLossless =
        normalizedFormats.some((format) => format.includes('FLAC') || format.includes('ALAC')) ||
        hasFlacCodec ||
        hasAlacCodec;

    return {
        codecs,
        formats: normalizedFormats,
        hasAtmos:
            normalizedFormats.includes('EAC3_JOC') ||
            normalizedText.includes('eac3_joc') ||
            normalizedText.includes('joc') ||
            codecs.some((codec) => codec.includes('ec-3') || codec.includes('eac3')),
        hasHiResLossless:
            normalizedFormats.includes('FLAC_HIRES') ||
            normalizedFormats.includes('HI_RES_LOSSLESS') ||
            normalizedText.includes('flac_hires') ||
            normalizedText.includes('hi_res') ||
            ((hasFlacCodec || hasAlacCodec) && hasHiResSampleRate(manifestText)),
        hasLossless,
    };
}

function resolveHlsUrl(baseUrl: string, url: string): string {
    try {
        return new URL(url, baseUrl).href;
    } catch {
        return url;
    }
}

function isPlaylistReference(url: string, line: string, previousTag: string): boolean {
    const lowerUrl = url.toLowerCase();
    const upperLine = line.trim().toUpperCase();
    const upperPreviousTag = previousTag.trim().toUpperCase();

    return (
        lowerUrl.includes('.m3u8') ||
        upperPreviousTag.startsWith('#EXT-X-STREAM-INF') ||
        upperLine.startsWith('#EXT-X-MEDIA') ||
        upperLine.startsWith('#EXT-X-I-FRAME-STREAM-INF')
    );
}

async function rewriteUriAttributes(
    line: string,
    baseUrl: string,
    previousTag: string,
    rewriteUrl: RewriteUrl,
    rewritePlaylistUrl: RewriteUrl
): Promise<string> {
    const matches = [...line.matchAll(/URI="([^"]+)"/g)];
    if (matches.length === 0) return line;

    let rewritten = '';
    let lastIndex = 0;
    for (const match of matches) {
        const [fullMatch, url] = match;
        const index = match.index ?? 0;
        const absoluteUrl = resolveHlsUrl(baseUrl, url);
        const replacementUrl = isPlaylistReference(url, line, previousTag)
            ? await rewritePlaylistUrl(absoluteUrl)
            : await rewriteUrl(absoluteUrl);

        rewritten += line.slice(lastIndex, index);
        rewritten += fullMatch.replace(url, replacementUrl);
        lastIndex = index + fullMatch.length;
    }

    rewritten += line.slice(lastIndex);
    return rewritten;
}

export async function rewriteHlsManifestUris(
    manifestText: string,
    baseUrl: string,
    rewriteUrl: RewriteUrl,
    rewritePlaylistUrl: RewriteUrl = rewriteUrl
): Promise<string> {
    const lines = manifestText.split(/\r?\n/);
    const rewrittenLines: string[] = [];
    let previousTag = '';

    for (const line of lines) {
        const trimmed = line.trim();
        let rewrittenLine = await rewriteUriAttributes(line, baseUrl, previousTag, rewriteUrl, rewritePlaylistUrl);

        if (trimmed && !trimmed.startsWith('#')) {
            const leadingWhitespace = line.match(/^\s*/)?.[0] || '';
            const absoluteUrl = resolveHlsUrl(baseUrl, trimmed);
            const replacementUrl = isPlaylistReference(trimmed, line, previousTag)
                ? await rewritePlaylistUrl(absoluteUrl)
                : await rewriteUrl(absoluteUrl);
            rewrittenLine = `${leadingWhitespace}${replacementUrl}`;
        }

        rewrittenLines.push(rewrittenLine);
        if (trimmed.startsWith('#')) {
            previousTag = trimmed;
        } else if (trimmed) {
            previousTag = '';
        }
    }

    return rewrittenLines.join('\n');
}
